import { Component, Input } from '@angular/core';
import { LiveRecordingsService } from 'src/app/services/live-recordings/live-recordings.service';
import { finalize } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-recording-trim',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './recording-trim.component.html',
  styleUrls: ['./recording-trim.component.css']
})
export class RecordingTrimComponent {
  @Input() filename = '';
  @Input() videoUrl = '';

  start = 0;
  end = 10;
  duration = 0;
  currentTime = 0;

  loading = false;
  errorMessage = '';
  successMessage = '';

  previewClipUrl: string | null = null;
  previewClipFilename: string | null = null;

  publicBase = 'https://live.sca.ge';

  constructor(private recordingsService: LiveRecordingsService) {}

  onLoadedMetadata(video: HTMLVideoElement): void {
    this.duration = Number(video.duration || 0);
    this.currentTime = Number(video.currentTime || 0);

    if (this.duration > 0) {
      this.end = Math.min(Math.floor(this.duration), this.end || Math.floor(this.duration));
      if (this.end <= this.start) {
        this.end = Math.floor(this.duration);
      }
    }
  }

  onTimeUpdate(video: HTMLVideoElement): void {
    this.currentTime = Number(video.currentTime || 0);
  }

  onStartSliderChange(video: HTMLVideoElement): void {
    if (this.start >= this.end) {
      this.start = Math.max(0, this.end - 0.1);
    }
    this.seekTo(video, this.start);
  }

  onEndSliderChange(): void {
    if (this.end <= this.start) {
      this.end = this.start + 0.1;
    }

    if (this.duration > 0 && this.end > this.duration) {
      this.end = this.duration;
    }
  }

  seekTo(video: HTMLVideoElement, value: number): void {
    const safeValue = Math.max(0, Math.min(this.duration || 0, Number(value || 0)));
    video.currentTime = safeValue;
    this.currentTime = safeValue;
  }

  previewSelection(video: HTMLVideoElement): void {
    if (!this.duration) return;
    this.seekTo(video, this.start);
    video.play().catch(() => {});
  }

  formatTime(totalSeconds: number): string {
    const sec = Math.max(0, Math.floor(totalSeconds || 0));
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;

    const hh = String(h).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    const ss = String(s).padStart(2, '0');

    return `${hh}:${mm}:${ss}`;
  }

  getTimelineMarks(): number[] {
    if (!this.duration) return [0];
    const steps = 4;
    const part = this.duration / steps;
    return Array.from({ length: steps + 1 }, (_, i) => Math.round(part * i));
  }

  private buildClipUrl(filename: string): string {
    return `${this.publicBase}/recordings/clips/${filename}`;
  }

  trim(): void {
    this.errorMessage = '';
    this.successMessage = '';

    if (!this.filename) {
      this.errorMessage = 'ფაილი არ არის არჩეული';
      return;
    }

    if (this.end <= this.start) {
      this.errorMessage = 'დასასრული უნდა იყოს დაწყებაზე მეტი';
      return;
    }

    this.loading = true;

    this.recordingsService.trimRecording({
      filename: this.filename,
      start: this.start,
      end: this.end
    })
    .pipe(finalize(() => this.loading = false))
    .subscribe({
      next: (res) => {
        this.previewClipFilename = res.item.filename;
        this.previewClipUrl = (res.item as any).url || this.buildClipUrl(res.item.filename);
        this.successMessage = 'კადრი მზადაა. შეგიძლია გადაამოწმო და შეინახო.';
      },
      error: (err) => {
        this.errorMessage = err?.error?.message || 'შეცდომა დაფიქსირდა';
      }
    });
  }

  saveReplace(): void {
    if (!this.filename || !this.previewClipFilename) return;

    this.loading = true;
    this.recordingsService.replaceRecording({
      original_filename: this.filename,
      clip_filename: this.previewClipFilename
    })
    .pipe(finalize(() => this.loading = false))
    .subscribe({
      next: () => {
        this.successMessage = 'ვიდეო წარმატებით ჩანაცვლდა';
        this.videoUrl = this.previewClipUrl || this.videoUrl;
        this.filename = this.previewClipFilename!;
        this.previewClipFilename = null;
      },
      error: (err) => {
        this.errorMessage = err?.error?.message || 'ჩანაცვლება ვერ მოხერხდა';
      }
    });
  }

cancelPreview(): void {

  if (this.previewClipFilename) {

    this.recordingsService.deleteTempClip(this.previewClipFilename)
      .subscribe({
        next: () => {
          // optional log
        },
        error: () => {
          console.warn('temp clip delete failed');
        }
      });
  }

  this.previewClipUrl = null;
  this.previewClipFilename = null;
  this.successMessage = '';
}
}